
import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable, signal } from "@angular/core";
import { IAppConfig, ILinkSettings, defaultconfig } from "./models/app-config.model";
import { environment } from 'src/environments/environment';
import { Features } from './models/features';
import { ReturnSymbol } from './models/performance';
import { IEnvironmentConfig, IEnvironmentSettings, ICognitoSettings } from './models/environmentConfig';
import { IFeatureSettings, IReportFeatureSettings } from './models/featuresConfig';

export enum WidgetComponent {
    PortfolioValue = "portfolioValue",
    Performance = "performance",
    Summary = "summary",
    Holdings = "holdings",
    Deposit = "deposit",
    Wallet = "wallet",
    BuySell = "buySell",
    InvestmentFunds = "investmentFunds",
    EntityOverview = "entityOverview",
    Withdrawal = "withdrawal",
    Returns = "returns",
    CashOverview = "cashOverview",
    TotalInvestment = "totalInvestment",
    PortfolioOverview = "portfolioOverview",
    Notifications = "notifications",
    PerformanceV2 = "performanceV2",
    HoldingsV2 = "holdingsV2",
    PortfolioGrowth = "portfolio-growth",
    SignUp = "signUp",
    KiwiSaverSignUp = "kiwiSaverSignUp",
    MaturityProfile = "maturityProfile",
}

export class PerformanceWidgetOption {
    dollarsVsPercentEnabled: boolean;
    inceptionText: string;
    defaultReturnSymbol: ReturnSymbol;
    hideHelp: boolean;
    hideTotalInvestment: boolean;
}

export class PortfolioGrowthWidgetOptions {
    growthPortfolioValueColour: string;
    growthTotalInvestmentColour: string;
}

export class DepositWidgetOptions {
    blurb: string;
}

export class WithdrawalWidgetOptions {
    blurb: string;
}

export class TotalInvestmentWidgetOptions {
    inceptionText: string;
}

export class BuySellWidgetOptions {
    showRegularInvestmentPlan: boolean;
    showFundsSection: boolean;
    maxWidgetSize: number;
    infoText: string;
    widgetText: string;
}

export class WalletWidgetOptions {
    displayDepositAndWithdrawalButtons: boolean;
    displaySubHeadingText: boolean;
    displayBalance: boolean;
    displayDisclaimer: boolean;
    disclaimer: string;
    availableCashHelpText: string;
}

export class SignUpWidgetOptions {
    signUpBlurb: string;
    signUpLink: string;
    signUpButtonText: string;
}

export class KiwiSaverSignUpWidgetOptions {
    signUpBlurb: string;
    signUpButtonText: string;
}

@Injectable()
export class AppConfig {

    static settings: IAppConfig = defaultconfig;

    constructor(private http: HttpClient) {
    }

    public appConfigSignal = signal(AppConfig.settings);

    /**
     * Loads configuration options pulled from the json config files as well as environment values.
     * @param appName the name of the app which can be used to infer the config to load.
     * @param configName the name of the config to be loaded if we want to explicitly choose one.
     * @param loadEnvironmentConfig controls whether to load the environment config. This can interfere with API calls so
     * should only be done on intitlisation of the app, not on reloads.
     * @returns
     */
    public loadConfig(appName?: string, configName?: string, loadEnvironmentConfig: boolean = true) {
        let configPathName = this.retrieveConfigPath(appName, configName);

        return new Promise<void>((resolve, reject) => {
            this.http.get(configPathName).toPromise()
                .then((res: IAppConfig) => {

                    if(!loadEnvironmentConfig){
                        AppConfig.settings.features = {...defaultconfig.features, ...<IFeatureSettings>res.features};
                        AppConfig.settings.env = { ...defaultconfig.env, ...<IEnvironmentSettings>res.env };
                        AppConfig.settings.links = { ...defaultconfig.links, ...<ILinkSettings>res.links };
                    } else {
                        AppConfig.settings = {...defaultconfig, ...res};
                        AppConfig.settings.features = {...defaultconfig.features, ...<IFeatureSettings>res.features};

                        this.loadEnvironmentConfig();
                    }

                    this.setStyleValues(res);
                    this.setFavicon(res);

                    //To ensure the signal actually fires we need to emit a new object, rather than emitting the 
                    //static settings object that has been mutated. This is because signals only emit when they
                    //detect the value is different.
                    this.appConfigSignal.set(this.createNewConfig());

                    resolve();
                })
                .catch((err: any) => {
                    reject(`Could not load configuration '${configPathName}': ${JSON.stringify(err)}`);
                });
        });
    }

    private createNewConfig() : IAppConfig {
        let appConfig = {} as IAppConfig;
        appConfig.features = {...AppConfig.settings.features};
        appConfig.env = {...AppConfig.settings.env};
        appConfig.links = {...AppConfig.settings.links};
        appConfig.cognito = {...AppConfig.settings.cognito};
        appConfig.environmentConfig = {...AppConfig.settings.environmentConfig};
        return appConfig;
    }

    /**
     * Detemines which config file to load based on the URL.
     * @returns
     */
    public getManagerContext() {
        var websiteUrl = window.location.href.replace(/(^\w+:|^)\/\//, '').replace('-', '.');
        var urlArray = websiteUrl.split('.');
        if (urlArray.includes('my')) {
            urlArray[0] = 'myadminis';
        } else if (urlArray.includes('investnowv2')) {
            urlArray[0] = 'investnowv2';
        } else if (urlArray.includes('investnow')) {
            urlArray[0] = 'investnow';
        } else if (urlArray.includes('stewartgroup')) {
            urlArray[0] = 'acifunds';
        } else if (urlArray.includes('ubt')) {
            urlArray[0] = 'vision';
        } else if (urlArray.includes('myfiduciary')) {
            urlArray[0] = 'mapuawealth';
        } else if (urlArray.includes('welcome')) {
            urlArray[0] = 'welcome';
        }

        return urlArray[0];
    }

    /**
     * Loads the environment config which populates urls for making API requests.
     */
    private loadEnvironmentConfig() {
        this.http.get('/assets/config/config.environment.json').toPromise()
            .then((res: IEnvironmentConfig) => {
                AppConfig.settings.environmentConfig = res;
                if (!environment.production) {
                    AppConfig.settings.environmentConfig.loginFeUrl = environment.loginFeUrl;
                    AppConfig.settings.environmentConfig.loginApiUrl = environment.loginApiUrl;
                    AppConfig.settings.environmentConfig.loginApiUrlBase = environment.loginApiUrlBase;
                    AppConfig.settings.environmentConfig.webApiUrl = environment.webApiUrl
                    AppConfig.settings.environmentConfig.loginApiCognitoUrl = environment.loginApiCognitoUrl;
                    AppConfig.settings.environmentConfig.redirectUrlSso = environment.redirectUrlSso;
                    AppConfig.settings.environmentConfig.appName = environment.appName;
                }
            })
            .catch((err: any) => {
                console.log(`Could not load configuration environment: ${JSON.stringify(err)}`);
            });
    }

    /**
     * Resolves the correct config file path name.
     * If not given a specific app name or a specific config name it will resolve which config file to use
     * based on the url.
     * @param appName the name of the application.
     * @param configName the specific name of a config. Passed in if we want to override the default config for the application.
     * @returns
     */
    private retrieveConfigPath(appName?: string, configName?: string): string {
        if (appName)
            return `/assets/config/config.${environment.appName}.json`;

        if (configName)
            return `/assets/config/config.${configName}.json`;

        return `/assets/config/config.${this.getManagerContext()}.json`;
    }

    /**
     * Overrides variable CSS values with settings from the config.
     * @param res
     */
    private setStyleValues(res: IAppConfig): void {
        document.documentElement.style.setProperty('--primary-color', res.env.primaryColor);
        document.documentElement.style.setProperty('--secondary-color', res.env.secondaryColor);
        document.documentElement.style.setProperty('--company-logo', res.env.companyLogo);

        // Set company logo width overrides if they exist
        if (res.env.companyLogoWidth)
            document.documentElement.style.setProperty('--company-logo-width', res.env.companyLogoWidth);

        if (res.env.companyLogoHeight)
            document.documentElement.style.setProperty('--company-logo-height', res.env.companyLogoHeight);

        if (res.env.companyLogoWidthMobile)
            document.documentElement.style.setProperty('--company-logo-width-mobile', res.env.companyLogoWidthMobile);

        if (res.env.companyLogoHeightMobile)
            document.documentElement.style.setProperty('--company-logo-height-mobile', res.env.companyLogoHeightMobile);

        // set company banner text, if not set then default to white
        if (res.env.companyBannerTextColor) {
            document.documentElement.style.setProperty('--company-banner-text-color', res.env.companyBannerTextColor)
        }
        else {
            document.documentElement.style.setProperty('--company-banner-text-color', 'white')
        }

        // set company banner text, if not set then default to white
        if (res.env.companyBannerTextColor) {
            document.documentElement.style.setProperty('--company-banner-text-color', res.env.companyBannerTextColor)
        }
        else {
            document.documentElement.style.setProperty('--company-banner-text-color', 'white')
        }

        document.documentElement.style.setProperty('--company-banner', res.env.companyBanner);
        document.documentElement.style.setProperty('--company-icon', res.env.companyIcon);
        document.documentElement.style.setProperty('--auth-image', res.env.authImage);

        document.documentElement.style.setProperty('--public-header-color', res.env.publicHeaderColor ?? '');
        document.documentElement.style.setProperty('--company-logo-white', res.env.companyLogoWhite);
        document.documentElement.style.setProperty('--header-text-color', res.env.headerTextColor);

        if (res.env.companyBannerLogo) {
            document.documentElement.style.setProperty('--company-banner-logo', res.env.companyBannerLogo);
        } else {
            document.documentElement.style.setProperty('--company-banner-logo', res.env.companyLogo);
        }

        if (res.env.companyBannerHighlightColor) {
            document.documentElement.style.setProperty('--company-banner-highlight-color', res.env.companyBannerHighlightColor);
        } else {
            document.documentElement.style.setProperty('--company-banner-highlight-color', res.env.primaryColor);
        }

		/* Optional configuration options to set "actionable" elements such as buttons, links and inputs. */
        if (res.env.actionColor) {
            document.documentElement.style.setProperty('--action-color', res.env.actionColor);
        }

        if (res.env.actionColorAlt) {
            document.documentElement.style.setProperty('--action-color-alt', res.env.actionColorAlt);
        }

        if (res.env.actionColorHover) {
            document.documentElement.style.setProperty('--action-color-hover', res.env.actionColorHover);
        }

        if (res.env.actionColorHoverAlt) {
            document.documentElement.style.setProperty('--action-color-hover-alt', res.env.actionColorHoverAlt);
        }

        if (res.env.actionColorDisabled) {
            document.documentElement.style.setProperty('--action-color-disabled', res.env.actionColorDisabled);
        }

        if (res.env.actionColorDisabledAlt) {
            document.documentElement.style.setProperty('--action-color-disabled-alt', res.env.actionColorDisabledAlt);
        }
        
    	/* Optional custom styles to tweak things which are not worth the effort or tricky to put into config 
    	   but easy to adjust using some simple CSS rules.  We dont want the site to break if this fails 
    	   so for safety just log on failure.  Another approach would be to include a configured css for the client.
           That is a common approach but didnt do that as suspect it would lead to abuse and overuse.
           So this approach solves a problem but encourages the use of only _small_ and _simple_ rules as
           anything else becomes intentionally impractical.
    	 */
        if (res.env.customStyles?.length) {
        	try {
	        	var css = res.env.customStyles.join('\n');
	        	
				var styleElement = document.createElement('style');
				styleElement.setAttribute('type', 'text/css');				
                styleElement.textContent = css;
	
				document.head.appendChild(styleElement);
			} catch(ex) {
				console.error(ex);
			}
        }

        if (res.env.colourPalette?.length)
            for (let i = 0; i < 30; i++) {
                const index = i % res.env.colourPalette.length;
                document.documentElement.style.setProperty(`--colour-palette-${i}`, res.env.colourPalette[index]);
            };

        if (res.env.buyImage){
            document.documentElement.style.setProperty('--buy-image', `url(${res.env.buyImage})`);
        }
        if (res.env.sellImage){
            document.documentElement.style.setProperty('--sell-image', `url(${res.env.sellImage})`);
        }
    }

    private setFavicon(res: IAppConfig): void {
        var link = document.createElement('link');
        var oldLink = document.getElementById('dynamic-favicon');
        link.id = 'dynamic-favicon';
        link.rel = 'shortcut icon';
        link.href = res.env.favicon;
        if (oldLink) {
            document.head.removeChild(oldLink);
        }
        document.head.appendChild(link);
    }

    setAllowIndividualConfig(showInvestorReport: boolean) {
        AppConfig.settings.features.reports.allowInvestor = showInvestorReport;
    }

    getIndividualReportsEnabled(): boolean {
        return AppConfig.settings.features.reports.allowInvestor;
    }

    //
    // get report settings, returns default if not set
    getReportSettings(isAdministrator: boolean): IReportFeatureSettings {

        // get report feature section
        let reportFeatureSection = this.getSection(Features.REPORTS);

        // setup defaults, doing same for both admin/investor for starters
        const defaultReportSettings = {
            onlyAllowSelectedInvestorsOnSelection: false,
            allHoldingsReportEnabled: false,
            generalLedgerReport: false,
            investmentReport: true,
            investorListReportEnabled: false,
            managementAccounts: false,
            memberListReportEnabled: false,
            portfolioPerformanceReportEnabled: false,
            taxSummaryReportEnabled: false,
            apsReportEnabled: false,
            capitalMovementsReport: false
        };

        // if administrator, get administrator section
        let reportSettings = undefined;
        if (isAdministrator) {
            reportSettings = reportFeatureSection?.hasOwnProperty('administrator') ? reportFeatureSection['administrator'] : defaultReportSettings;
        }
        // else investor, look for investor section
        else {
            reportSettings = reportFeatureSection?.hasOwnProperty('investor') ? reportFeatureSection['investor'] : defaultReportSettings;
        }

        return reportSettings;
    }

    getIndividualDocLibEnabled(): boolean {
        return AppConfig.settings.features.documentLibrary.allowInvestor;
    }

    setIndividualDocLibEnabled(showDocLibrary: boolean) {
        AppConfig.settings.features.documentLibrary.allowInvestor = showDocLibrary;
    }

    setIndividualBuySellEnabled(showBuySell: boolean) {
        AppConfig.settings.features.buySell.allowInvestor = showBuySell;
        // TODO, when we update to typescript 3.7, this should be changed into an optional chain
        // https://www.typescriptlang.org/docs/handbook/release-notes/typescript-3-7.html
        if (AppConfig.settings.features.dashboard &&
            AppConfig.settings.features.dashboard.widgets) {
            const buySells = AppConfig.settings.features.dashboard.widgets.find((w) => w.componentId === WidgetComponent.BuySell);
            if (buySells && buySells.options && buySells.options) {
                buySells.options.allowInvestor = showBuySell;
            }
        }
    }

    setWithdrawalsEnabledForManagerUsers(allowWithdrawals: boolean) {
        var hasWithdrawalFeatureSetting = AppConfig.settings?.features?.withdrawals ? true : false;
        if (hasWithdrawalFeatureSetting)
            AppConfig.settings.features.withdrawals.allowManager = allowWithdrawals;
    }

    setWithdrawalsEnabledForInvestor(allowWithdrawals: boolean) {
        AppConfig.settings.features.withdrawals.allowInvestor = allowWithdrawals;
    }

    public isWithdrawalsEnabledForManagerUsers() {
        let hasWithdrawalFeatureSettings = !!AppConfig.settings?.features?.withdrawals;
        if (hasWithdrawalFeatureSettings)
            return AppConfig.settings.features.withdrawals.enabled && AppConfig.settings.features.withdrawals.allowManager;
        return false;
    }

    public isWithdrawalsEnabledForInvestorUsers() {
        let hasWithdrawalFeatureSettings = !!AppConfig.settings?.features?.withdrawals;
        if (hasWithdrawalFeatureSettings)
            return AppConfig.settings.features.withdrawals.enabled && AppConfig.settings.features.withdrawals.allowInvestor;
        return false;
    }

    getIndividualBuySellEnabled() {
        return AppConfig.settings.features.buySell.allowInvestor;
    }

    getEnvironmentConfig(): IEnvironmentConfig {
        return AppConfig.settings.environmentConfig;
    }

    getEnvironment(): IEnvironmentSettings {
        return AppConfig.settings.env;
    }

    getLinks(): ILinkSettings {
        return AppConfig.settings.links;
    }

    getCognitoSettings(): ICognitoSettings {
        const appName = environment.production ? this.getManagerContext() : environment.appName;
        return AppConfig.settings[appName]?.cognito;
    }

    getSection(feature: string): any {

        return AppConfig.settings.features.hasOwnProperty(feature) ? AppConfig.settings.features[feature] : undefined;
    }

    getLinkText(feature: string, defaultText: string): string {
        const section = this.getSection(feature);
        return section && section.linkText ? section.linkText : defaultText;
    }

    // TODO: #featurepermissions
    getPerformanceWidgetOptions(): PerformanceWidgetOption {
        if (AppConfig.settings.features.dashboard &&
            AppConfig.settings.features.dashboard.widgets) {
            const performance = AppConfig.settings.features.dashboard.widgets.find((w) => w.componentId === WidgetComponent.PerformanceV2);
            if (performance && performance.options) {
                const option = new PerformanceWidgetOption();
                option.dollarsVsPercentEnabled = performance.options.hasOwnProperty("dollarsVsPercentEnabled") ? performance.options["dollarsVsPercentEnabled"] : true;
                option.defaultReturnSymbol = performance.options.hasOwnProperty("defaultReturnSymbol") ? performance.options["defaultReturnSymbol"] : "%";
                option.inceptionText = performance.options.hasOwnProperty("inceptionText") ? performance.options["inceptionText"] : "Since inception";
                option.hideHelp = performance.options.hasOwnProperty('hideHelp') ? performance.options["hideHelp"] : false;
                return option;
            }
        }
    }

    getPortfolioGrowthWidgetOptions() : PortfolioGrowthWidgetOptions {
        if (AppConfig.settings.features.dashboard && AppConfig.settings.features.dashboard.widgets) {
            const portfolioGrowth = AppConfig.settings.features.dashboard.widgets.find((w) => w.componentId === WidgetComponent.PortfolioGrowth);
            if (portfolioGrowth && portfolioGrowth.options) {
                const options = new PortfolioGrowthWidgetOptions();
                options.growthPortfolioValueColour = portfolioGrowth.options["growthPortfolioValueColour"];
                options.growthTotalInvestmentColour = portfolioGrowth.options["growthTotalInvestmentColour"];
                return options;
            }
        }
    }

    getDepositWidgetOptions() : DepositWidgetOptions {
        if(AppConfig.settings.features.dashboard && AppConfig.settings.features.dashboard.widgets){
            const deposit = AppConfig.settings.features.dashboard.widgets.find((w) => w.componentId === WidgetComponent.Deposit);
            if(deposit && deposit.options){
                const options = new DepositWidgetOptions();
                options.blurb = deposit.options["blurb"];
                return options;
            }
        }
    }

    getWithdrawalWidgetOptions(): WithdrawalWidgetOptions {
        if (AppConfig.settings.features.dashboard && AppConfig.settings.features.dashboard.widgets) {
            const withdrawal = AppConfig.settings.features.dashboard.widgets.find((w) => w.componentId === WidgetComponent.Withdrawal);
            if (withdrawal && withdrawal.options) {
                const options = new WithdrawalWidgetOptions();
                options.blurb = withdrawal.options["blurb"];
                return options;
            }
        }
    }

    getTotalInvestmentWidgetOptions(): TotalInvestmentWidgetOptions {
        if (AppConfig.settings.features.dashboard && AppConfig.settings.features.dashboard.widgets) {
            const totalInvestment = AppConfig.settings.features.dashboard.widgets.find((w) => w.componentId === WidgetComponent.TotalInvestment);
            if (totalInvestment && totalInvestment.options) {
                const options = new TotalInvestmentWidgetOptions();
                options.inceptionText = totalInvestment.options["inceptionText"];
                return options;
            }
        }
    }

    getBuySellWidgetOptions(): BuySellWidgetOptions {
        if (AppConfig.settings.features.dashboard && AppConfig.settings.features.dashboard.widgets) {
            const buySell = AppConfig.settings.features.dashboard.widgets.find((w) => w.componentId === WidgetComponent.BuySell);
            if (buySell && buySell.options) {
                const options = new BuySellWidgetOptions();
                options.showRegularInvestmentPlan = buySell.options["showRegularInvestmentPlan"] ?? true;
                options.showFundsSection = buySell.options["showFundsSection"] ?? true;
                options.maxWidgetSize = buySell.options["maxWidgetSize"] ?? 12;
                options.infoText = buySell.options["infoText"] ?? "You’ll be able to see all funds that you’ve invested in, what funds are available to invest in and the ability to buy or sell funds.";
                options.widgetText = buySell.options["widgetText"] ?? "";
                return options;
            }
        }
    }

    getWalletWidgetOptions(): WalletWidgetOptions {
        if (AppConfig.settings.features.dashboard && AppConfig.settings.features.dashboard.widgets) {
            const wallet = AppConfig.settings.features.dashboard.widgets.find((w) => w.componentId === WidgetComponent.Wallet);
            if (wallet && wallet.options) {
                const options = new WalletWidgetOptions();
                options.displayDepositAndWithdrawalButtons = wallet.options["displayDepositAndWithdrawalButtons"];
                options.displaySubHeadingText = wallet.options["displaySubHeadingText"];
                options.displayBalance = wallet.options["displayBalance"];
                options.displayDisclaimer = wallet.options["displayDisclaimer"];
                options.disclaimer = wallet.options["disclaimer"];
                options.availableCashHelpText = wallet.options["availableCashHelpText"];
                return options;
            }
        }
    }

    getSignUpWidgetOptions() : SignUpWidgetOptions {
        if (AppConfig.settings.features.dashboard && AppConfig.settings.features.dashboard.widgets) {
            const signUpWidgetOptions = AppConfig.settings.features.dashboard.widgets.find((w) => w.componentId === WidgetComponent.SignUp);
            if (signUpWidgetOptions && signUpWidgetOptions.options) {
                const options = new SignUpWidgetOptions();
                options.signUpBlurb = signUpWidgetOptions.options["signUpBlurb"];
                options.signUpLink = signUpWidgetOptions.options["signUpLink"];
                options.signUpButtonText = signUpWidgetOptions.options["signUpButtonText"];
                return options;
            }
        }
    }
    
    getKiwiSaverSignUpWidgetOptions() : KiwiSaverSignUpWidgetOptions {
        if (AppConfig.settings.features.dashboard && AppConfig.settings.features.dashboard.widgets) {
            const signUpWidgetOptions = AppConfig.settings.features.dashboard.widgets.find((w) => w.componentId === WidgetComponent.KiwiSaverSignUp);
            if (signUpWidgetOptions && signUpWidgetOptions.options) {
                const options = new SignUpWidgetOptions();
                options.signUpBlurb = signUpWidgetOptions.options["signUpBlurb"];
                options.signUpButtonText = signUpWidgetOptions.options["signUpButtonText"];
                return options;
            }
        }
    }

    isFeaturePropertyEnabled(feature: Features, property: string, defaultEnabled = true): boolean {
        const featureSettings = this.getSection(feature);
        // property is by default enabled unless configured otherwise
        let propertyEnabled = defaultEnabled;
        if (featureSettings && featureSettings.enabled) {
            propertyEnabled = (featureSettings[property] && featureSettings[property].hasOwnProperty('enabled')) ? featureSettings[property].enabled : true;
        }
        return propertyEnabled;
    }

    isFeatureEnabled(feature: Features): boolean {
        const featureSettings = this.getSection(feature);
        return (featureSettings && featureSettings.enabled) ? true : false;
    }

    getFeatureProperty(feature: Features, property: string): string {
        const featureSettings = this.getSection(feature);
        let propertyValue;
        if (featureSettings && featureSettings.enabled) {
            propertyValue = featureSettings[property] ?? null;
        }
        return propertyValue;
    }

    getValueOrDefault(feature: Features, property: string, defaultValue: string): string {
        const propertyValue = this.getFeatureProperty(feature, property);
        if (propertyValue) {
            return propertyValue;
        } else {
            return defaultValue;
        }
    }

    getTaxDetailsUpdateMessage() {
        return AppConfig.settings.features.taxDetails.taxDetailsUpdateMessge;
    }


    private defaultProductName: string = 'Fund';
    private defaultPluralProductName: string = 'Funds';

    public getAllLowerProductName(): string {
        return (AppConfig.settings.env.productName ?? this.defaultProductName).toLowerCase();
    }

    public getCapitalizedProductName(): string {
        return this.capitalizeFirstLetter((AppConfig.settings.env.productName ?? this.defaultProductName));
    }

    public getAllLowerPluralProductName() {
        return (AppConfig.settings.env.pluralProductName ?? this.defaultPluralProductName).toLowerCase();
    }

    public getCapitalizedPluralProductName() {
        return this.capitalizeFirstLetter(AppConfig.settings.env.pluralProductName ?? this.defaultPluralProductName);
    }


    private capitalizeFirstLetter(val: string) {
        return String(val).charAt(0).toUpperCase() + String(val).slice(1);
    }

}